import { useState, useEffect } from "react"
import PushNotificationSubscriberService from "@/services/PushNotificationSubscriber"

export const pushSupported =
	typeof window !== "undefined" &&
	"serviceWorker" in navigator &&
	"PushManager" in window

function isPermissionDenied (): boolean {
	return "Notification" in window && window.Notification.permission === "denied"
}

type UsePushNotificationReturn = {
	pushSupported: boolean
	pushEnabled: boolean
	pushPermissionDenied: boolean
	subscribe: () => Promise<PushSubscription | null>
	unsubscribe: () => Promise<void>
	togglePush: () => Promise<void>
}

const usePushNotification = (): UsePushNotificationReturn => {
	const [pushEnabled, setPushEnabled] = useState(false)
	const [pushPermissionDenied, setPushPermissionDenied] = useState(false)

	useEffect(() => {
		if (!pushSupported) return

		if (isPermissionDenied()) {
			setPushPermissionDenied(true)
			return
		}

		navigator.serviceWorker.ready.then((registration) => {
			registration.pushManager.getSubscription().then(async (subscription) => {
				if (subscription) {
					await PushNotificationSubscriberService.subscribe()
				}
				setPushEnabled(!!subscription)
			})
		})

		if (!("permissions" in navigator)) return

		let permissionStatus: PermissionStatus | null = null

		navigator.permissions.query({ name: "notifications" }).then((status) => {
			permissionStatus = status
			status.onchange = () => {
				if (status.state === "granted") {
					setPushPermissionDenied(false)
					navigator.serviceWorker.ready.then((registration) => {
						registration.pushManager.getSubscription().then((sub) => {
							setPushEnabled(!!sub)
						})
					})
				} else if (status.state === "denied") {
					setPushPermissionDenied(true)
					setPushEnabled(false)
				}
			}
		})

		return () => {
			if (permissionStatus) {
				permissionStatus.onchange = null
			}
		}
	}, [])

	const subscribe = async (): Promise<PushSubscription | null> => {
		if (isPermissionDenied()) {
			setPushPermissionDenied(true)
			return null
		}

		const subscription = await PushNotificationSubscriberService.subscribe()

		if (subscription) {
			setPushEnabled(true)
		}

		return subscription
	}

	const unsubscribe = async (): Promise<void> => {
		await PushNotificationSubscriberService.unsubscribe()
		setPushEnabled(false)
	}

	const togglePush = async (): Promise<void> => {
		if (pushEnabled) {
			await unsubscribe()
		} else {
			const permission = await window.Notification.requestPermission()
			if (permission !== "granted") {
				setPushPermissionDenied(permission === "denied")
				return
			}
			const sub = await PushNotificationSubscriberService.subscribe()
			setPushEnabled(!!sub)
		}
	}

	return {
		pushSupported,
		pushEnabled,
		pushPermissionDenied,
		subscribe,
		unsubscribe,
		togglePush
	}
}

export default usePushNotification
